import type { SrpTopRolesStripItem } from '../../types/srp-top-roles-strip';

export type TopRolesStripApiResponse = {
  roles: SrpTopRolesStripItem[];
};

/**
 * Browser: loads the same top-roles list as SSR when the page payload has no strip
 * (e.g. client-side navigation into the SRP).
 */
export async function fetchTopRolesStripFromApi(locale: string, signal?: AbortSignal): Promise<SrpTopRolesStripItem[]> {
  try {
    const res = await fetch(`/api/top-roles-strip?locale=${encodeURIComponent(locale)}`, {
      method: 'GET',
      credentials: 'same-origin',
      signal,
    });
    if (!res.ok) return [];
    const json = (await res.json()) as TopRolesStripApiResponse;
    return Array.isArray(json?.roles) ? json.roles : [];
  } catch {
    return [];
  }
}

/** The page's locale from its URL (/fi/..., /sv/..., else en): role names and links are localized. */
function pathLocale(): string {
  if (typeof window === 'undefined') return 'en';
  const first = window.location.pathname.split('/')[1];
  return first === 'fi' || first === 'sv' ? first : 'en';
}

/** Session cache (per locale) so repeat SRP visits and concurrent callers share one network request. */
const memoryCache = new Map<string, SrpTopRolesStripItem[]>();
const inflight = new Map<string, Promise<SrpTopRolesStripItem[]>>();

/**
 * Single-flight + memory cache for the top-roles strip. Prefer this over {@link fetchTopRolesStripFromApi}
 * for UI so navigations do not refetch or race duplicate requests.
 */
export function loadTopRolesStripDeduped(locale: string = pathLocale()): Promise<SrpTopRolesStripItem[]> {
  const cached = memoryCache.get(locale);
  if (cached) {
    return Promise.resolve(cached);
  }
  const pending = inflight.get(locale);
  if (pending) {
    return pending;
  }
  const request = fetchTopRolesStripFromApi(locale)
    .then((roles) => {
      memoryCache.set(locale, roles);
      return roles;
    })
    .finally(() => {
      inflight.delete(locale);
    });
  inflight.set(locale, request);
  return request;
}
